import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { apiInstanceFetch } from "../../../util/api";
import defaultImage from "../../../assets/images/default.jpg";

// Phase 1B.1 — admin queue for ID verification submissions.
// Reviewer triages oldest-first; approve unlocks the user's Creator
// Mode, reject sends a reason that surfaces in the user's app.

const statusBadge = (s) => {
  switch (s) {
    case "pending":
      return { label: "Pending", color: "#F59E0B", bg: "#FEF3C7" };
    case "approved":
      return { label: "Approved", color: "#10B981", bg: "#D1FAE5" };
    case "rejected":
      return { label: "Rejected", color: "#EF4444", bg: "#FEE2E2" };
    case "resubmit":
      return { label: "New images requested", color: "#6366F1", bg: "#E0E7FF" };
    default:
      return { label: s || "—", color: "#6B7280", bg: "#F3F4F6" };
  }
};


// Table thumbnails: request a tiny 120px WebP from the /img proxy instead of the
// full 720px /storage rewrite — the queue loads ~30 scans at once and reviewers
// on slow links (e.g. abroad) saw every image stall out. Preview stays full-size.
const thumbUrl = (url) => {
  try {
    // Private KYC files (2026-10-12): the API sends signed links; just ask for a 120px thumb.
    if (String(url || "").includes("/kyc/file/")) return url + (url.includes("?") ? "&" : "?") + "w=120";
    const m = String(url || "").match(/^(https?:\/\/[^/]+)\/storage\/([^/?#]+\.(?:jpe?g|png|webp))$/i);
    return m ? `${m[1]}/img/${m[2]}?w=120` : url;
  } catch (e) {
    return url;
  }
};

// Risk badge coloring: green = strong signals, amber = mixed, red = weak/flagged.
const riskPct = (an) => (an && an.riskMax ? Math.round((100 * (an.riskScore || 0)) / an.riskMax) : null);
const riskStyle = (pct, flagged) => {
  if (flagged) return { color: "#EF4444", bg: "#FEE2E2" };
  if (pct >= 70) return { color: "#10B981", bg: "#D1FAE5" };
  if (pct >= 45) return { color: "#B45309", bg: "#FEF3C7" };
  return { color: "#EF4444", bg: "#FEE2E2" };
};
const FLAG_LABELS = {
  duplicate_face: "DUP FACE",
  duplicate_id_number: "DUP ID",
  no_face_on_id: "NO FACE",
  shared_device: "SHARED DEVICE",
  front_file_missing: "FILE MISSING",
  face_mismatch: "FACE MISMATCH",
  no_face_in_selfie: "NO FACE IN SELFIE",
  no_id_portrait_for_match: "NO ID PORTRAIT",
};
const HARD_FLAGS = ["duplicate_face", "duplicate_id_number", "face_mismatch"];

const KycQueue = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState("pending");
  // Server-side search (name/email/uniqueId/phone) — debounced so we don't
  // fire a request per keystroke.
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setQDebounced(q.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const [previewUrl, setPreviewUrl] = useState(null);

  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionUser, setDecisionUser] = useState(null);
  const [decisionChoice, setDecisionChoice] = useState("approved");
  const [decisionReason, setDecisionReason] = useState("");

  const [analysisUser, setAnalysisUser] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);

  const runAnalyze = async (u) => {
    const id = String(u._id);
    setAnalyzing(id);
    try {
      const res = await apiInstanceFetch.post(`kyc/analyze`, { userId: id });
      if (res?.status) {
        setRows((prev) => prev.map((r) => (String(r._id) === id ? { ...r, kycAnalysis: res.data } : r)));
        setAnalysisUser((prev) => (prev && String(prev._id) === id ? { ...prev, kycAnalysis: res.data } : prev));
      } else {
        alert(res?.message || "Analysis failed.");
      }
    } catch (e) {
      console.error("kyc/analyze error", e);
      alert("Analysis failed.");
    }
    setAnalyzing(null);
  };

  const [listLoading, setListLoading] = useState(false);

  const load = async () => {
    // Clear stale rows immediately — on slow links the previous filter's
    // rows lingered under the new chip and read as a broken filter.
    setListLoading(true);
    setRows([]);
    try {
      const res = await apiInstanceFetch.get(
        `kyc/queue?page=${page}&limit=${limit}&status=${status}&q=${encodeURIComponent(qDebounced)}`
      );
      if (res?.status) {
        setRows(res.data || []);
        setTotal(res.total || 0);
      }
    } catch (e) {
      console.error("kyc/queue error", e);
    }
    setListLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, status, qDebounced]);

  const openDecision = (u, choice) => {
    setDecisionUser(u);
    setDecisionChoice(choice);
    setDecisionReason("");
    setDecisionOpen(true);
  };

  const submitDecision = async () => {
    if (!decisionUser) return;
    if (decisionChoice !== "approved" && !decisionReason.trim()) {
      alert("A reason is required — it's shown to the user.");
      return;
    }
    try {
      const body = {
        userId: String(decisionUser._id),
        decision: decisionChoice,
        ...(decisionChoice !== "approved" ? { reason: decisionReason.trim() } : {}),
      };
      const res = await apiInstanceFetch.post(`kyc/decision`, body);
      if (res?.status) {
        setDecisionOpen(false);
        setDecisionUser(null);
        load();
      } else {
        alert(res?.message || "Decision failed.");
      }
    } catch (e) {
      console.error("kyc/decision error", e);
      alert("Decision failed.");
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>ID Verification Queue</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, ID, phone…"
            style={{
              padding: "7px 12px",
              borderRadius: 8,
              border: "1px solid #E5E7EB",
              minWidth: 240,
              outline: "none",
            }}
          />
          {["pending", "approved", "rejected", "resubmit", "all"].map((s) => (
            <button
              key={s}
              onClick={() => {
                setPage(1);
                setStatus(s);
              }}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                border: status === s ? "1px solid #1A1614" : "1px solid #E5E7EB",
                background: status === s ? "#1A1614" : "#FFF",
                color: status === s ? "#FFF" : "#1A1614",
                cursor: "pointer",
                textTransform: "capitalize",
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: "#FFFDF7", border: "1px solid #E8E1D3", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "#FAF7F0" }}>
            <tr>
              <th style={th}>User</th>
              <th style={th}>Doc Type</th>
              <th style={th}>Front</th>
              <th style={th}>Back</th>
              <th style={th}>Face</th>
              <th style={th}>Submitted</th>
              <th style={th}>Risk</th>
              <th style={th}>Status</th>
              <th style={th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 20, textAlign: "center", color: "#8A857F" }}>
                  {listLoading ? "Loading…" : "No submissions in this status."}
                </td>
              </tr>
            ) : (
              rows.map((u) => {
                const badge = statusBadge(u.kycStatus);
                const docs = u.kycDocuments || {};
                return (
                  <tr key={u._id} style={{ borderTop: "1px solid #F0EAD8" }}>
                    <td style={td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <img
                          src={u.image || defaultImage}
                          loading="lazy"
                          alt=""
                          style={{ width: 36, height: 36, borderRadius: 18, objectFit: "cover" }}
                        />
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {(u.firstName || "") + " " + (u.lastName || "")}
                          </div>
                          <div style={{ fontSize: 12, color: "#8A857F" }}>
                            {u.email || u.mobileNumber || u.uniqueId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={td}>{(docs.type || "—").replace(/_/g, " ")}</td>
                    <td style={td}>
                      {docs.frontUrl ? (
                        <img
                          src={thumbUrl(docs.frontUrl)}
                          loading="lazy"
                          alt="front"
                          style={{ width: 60, height: 40, objectFit: "cover", cursor: "pointer", borderRadius: 4 }}
                          onClick={() => setPreviewUrl(docs.frontUrl)}
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={td}>
                      {docs.backUrl ? (
                        <img
                          src={thumbUrl(docs.backUrl)}
                          loading="lazy"
                          alt="back"
                          style={{ width: 60, height: 40, objectFit: "cover", cursor: "pointer", borderRadius: 4 }}
                          onClick={() => setPreviewUrl(docs.backUrl)}
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={td}>
                      {(() => {
                        // Prefer the LIVE camera selfie (new capture flow);
                        // fall back to the portrait cropped off the ID card.
                        const selfie = u.kycDocuments?.selfieUrl;
                        const crop = u.kycAnalysis?.face?.cropUrl;
                        const url = selfie || crop;
                        if (!url) return "—";
                        return (
                          <img
                            src={thumbUrl(url)}
                            loading="lazy"
                            alt=""
                            title={selfie ? "Live camera selfie" : "Face from ID document"}
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: "cover",
                              borderRadius: 20,
                              cursor: "pointer",
                              border: selfie ? "2px solid #10B981" : "2px solid #E5E7EB",
                            }}
                            onClick={() => setPreviewUrl(url)}
                          />
                        );
                      })()}
                    </td>
                    <td style={td}>
                      {u.kycSubmittedAt ? dayjs(u.kycSubmittedAt).format("YYYY-MM-DD HH:mm") : "—"}
                    </td>
                    <td style={td}>
                      {u.kycAnalysis ? (
                        (() => {
                          const an = u.kycAnalysis;
                          const pct = riskPct(an);
                          const flagged = (an.flags || []).some((f) => HARD_FLAGS.includes(f));
                          const rs = riskStyle(pct, flagged);
                          return (
                            <div style={{ cursor: "pointer" }} onClick={() => setAnalysisUser(u)} title="Open analysis">
                              <span style={{ background: rs.bg, color: rs.color, padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700 }}>
                                {pct}%
                              </span>
                              {(an.flags || []).slice(0, 2).map((f) => (
                                <span key={f} style={{ marginLeft: 4, background: "#FEE2E2", color: "#B91C1C", padding: "2px 6px", borderRadius: 8, fontSize: 10, fontWeight: 700 }}>
                                  {FLAG_LABELS[f] || f}
                                </span>
                              ))}
                            </div>
                          );
                        })()
                      ) : (
                        <button
                          onClick={() => runAnalyze(u)}
                          disabled={analyzing === String(u._id)}
                          style={{ padding: "4px 10px", border: "1px solid #E5E7EB", borderRadius: 6, background: "#FFF", cursor: "pointer", fontSize: 12 }}
                        >
                          {analyzing === String(u._id) ? "Analyzing…" : "Analyze"}
                        </button>
                      )}
                    </td>
                    <td style={td}>
                      <span
                        style={{
                          background: badge.bg,
                          color: badge.color,
                          padding: "3px 10px",
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {badge.label}
                      </span>
                      {u.kycFraudStrikes > 0 && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: "#B91C1C", fontWeight: 700 }} title="Automatic fraud rejections (face ≠ selfie / face on another account); the account is blocked at 3">
                          ⚠ {u.kycFraudStrikes} strike{u.kycFraudStrikes > 1 ? "s" : ""}
                        </span>
                      )}
                      {((u.kycRetakes != null ? u.kycRetakes : u.kycNoFaceRetakes) || 0) > 0 && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: "#6366F1" }} title={"Automatic retake requests. History: " + ((u.kycAttempts || []).map((a, i) => (i + 1) + ") " + a.outcome + ": " + ((a.reasons || []).join(", ") || "clean")).join("  ") || "—")}>
                          ↻ {u.kycRetakes != null ? u.kycRetakes : u.kycNoFaceRetakes}
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      {u.kycStatus === "pending" || u.kycStatus === "resubmit" ? (
                        <>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => openDecision(u, "approved")} style={btnApprove}>
                            Approve
                          </button>
                          <button onClick={() => openDecision(u, "rejected")} style={btnReject}>
                            Reject
                          </button>
                          <button onClick={() => openDecision(u, "resubmit")} style={btnResubmit} title="Ask the user to retake their photos">
                            New images
                          </button>
                          {u.kycAnalysis && u.kycAnalysis.autoDecision && (
                            <span style={{ fontSize: 11, color: "#6366F1", alignSelf: "center" }} title={u.kycRejectionReason}>⚡ {u.kycAnalysis.autoDecision === "manual" ? "after failed attempts" : "auto retake"}</span>
                          )}
                        </div>
                        {u.kycStatus === "pending" && u.kycRejectionReason && (
                          <div style={{ fontSize: 11, color: "#8A857F", marginTop: 4, maxWidth: 360, whiteSpace: "normal" }}>{u.kycRejectionReason}</div>
                        )}
                        </>
                      ) : u.kycStatus === "approved" ? (
                        // An approval used to be final in the UI — a wrongly
                        // approved ID (or an auto-approval that got it wrong)
                        // could never be undone. The server always allowed a
                        // new decision; only these buttons were missing.
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => openDecision(u, "resubmit")}
                            style={btnResubmit}
                            title="Revoke this approval and ask the user for new photos"
                          >
                            Revoke &amp; ask again
                          </button>
                          <button
                            onClick={() => openDecision(u, "rejected")}
                            style={btnReject}
                            title="Revoke this approval and reject the verification"
                          >
                            Revoke
                          </button>
                        </div>
                      ) : u.kycStatus === "rejected" && u.kycRejectionReason ? (
                        <span style={{ fontSize: 12, color: "#8A857F" }} title={(u.kycAnalysis && u.kycAnalysis.autoDecision ? "Automatic: " : "") + u.kycRejectionReason}>
                          {u.kycAnalysis && u.kycAnalysis.autoDecision ? "⚡ " : ""}
                          {u.kycRejectionReason.length > 30
                            ? u.kycRejectionReason.slice(0, 30) + "…"
                            : u.kycRejectionReason}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Inline pager — the shared extra/Pagination expects different props
          (serverPage/onPageChange) and rendered nothing here. */}
      <div style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, color: "#8A857F" }}>
          {total === 0
            ? "0 results"
            : `${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total}`}
        </span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            onClick={() => setPage(1)}
            disabled={page <= 1}
            style={pagerBtn(page <= 1)}
          >
            «
          </button>
          <button
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            style={pagerBtn(page <= 1)}
          >
            ‹ Prev
          </button>
          <span style={{ fontSize: 13, fontWeight: 600, padding: "0 8px" }}>
            Page {page} of {Math.max(1, Math.ceil(total / limit))}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= Math.ceil(total / limit)}
            style={pagerBtn(page >= Math.ceil(total / limit))}
          >
            Next ›
          </button>
          <button
            onClick={() => setPage(Math.max(1, Math.ceil(total / limit)))}
            disabled={page >= Math.ceil(total / limit)}
            style={pagerBtn(page >= Math.ceil(total / limit))}
          >
            »
          </button>
        </div>
      </div>

      {analysisUser && analysisUser.kycAnalysis && (
        <div style={modalBg} onClick={() => setAnalysisUser(null)}>
          <div style={{ ...modalCard, width: 720, maxHeight: "88vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            {(() => {
              const an = analysisUser.kycAnalysis;
              const pct = riskPct(an);
              const flagged = (an.flags || []).some((f) => HARD_FLAGS.includes(f));
              const rs = riskStyle(pct, flagged);
              const scoreRow = (label, val, max) => (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #F0EAD8", fontSize: 13 }}>
                  <span>{label}</span>
                  <b>{val != null ? `${val} / ${max}` : "—"}</b>
                </div>
              );
              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3 style={{ margin: 0 }}>
                      {analysisUser.firstName} {analysisUser.lastName}
                      <span style={{ marginLeft: 10, background: rs.bg, color: rs.color, padding: "3px 12px", borderRadius: 12, fontSize: 14 }}>
                        {pct}% · {an.riskScore}/{an.riskMax}
                      </span>
                    </h3>
                    <button
                      onClick={() => runAnalyze(analysisUser)}
                      disabled={analyzing === String(analysisUser._id)}
                      style={{ padding: "4px 10px", border: "1px solid #E5E7EB", borderRadius: 6, background: "#FFF", cursor: "pointer", fontSize: 12 }}
                    >
                      {analyzing === String(analysisUser._id) ? "Re-running…" : "Re-run analysis"}
                    </button>
                  </div>
                  {(an.flags || []).length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      {an.flags.map((f) => (
                        <span key={f} style={{ marginRight: 6, background: "#FEE2E2", color: "#B91C1C", padding: "3px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700 }}>
                          {FLAG_LABELS[f] || f}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 16, marginTop: 14 }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#8A857F", marginBottom: 4 }}>ID portrait</div>
                      {an.face && an.face.cropUrl ? (
                        <img src={an.face.cropUrl} alt="" style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 8 }} />
                      ) : (
                        <div style={{ width: 110, height: 110, borderRadius: 8, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#8A857F" }}>
                          no face
                        </div>
                      )}
                    </div>
                    {analysisUser.kycDocuments?.selfieUrl ? (
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 11, color: "#8A857F", marginBottom: 4 }}>Live selfie</div>
                        <img
                          src={analysisUser.kycDocuments.selfieUrl}
                          alt=""
                          style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 8, cursor: "pointer" }}
                          onClick={() => setPreviewUrl(analysisUser.kycDocuments.selfieUrl)}
                        />
                        {an.selfie && an.selfie.matchPct != null && (
                          <div style={{ fontSize: 12, marginTop: 4 }}>
                            ID match <b>{an.selfie.matchPct}%</b>
                          </div>
                        )}
                        {an.selfie && (
                          <div style={{ fontSize: 11, marginTop: 2, color: an.selfie.livenessPassed ? "#047857" : "#B45309" }}>
                            {an.selfie.livenessPassed ? "liveness passed" : "no liveness"}
                          </div>
                        )}
                      </div>
                    ) : null}
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#8A857F", marginBottom: 4 }}>Profile photo</div>
                      <img src={analysisUser.image || defaultImage} alt="" style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 8 }} />
                      {an.profileFaceMatchPct != null && (
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          match <b>{an.profileFaceMatchPct}%</b>
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      {scoreRow("Image quality", an.scores?.imageQuality, 15)}
                      {scoreRow("OCR / document data", an.scores?.ocr, 20)}
                      {scoreRow("Device trust", an.scores?.deviceTrust, 10)}
                      {an.riskMax >= 100 && scoreRow("Face match (ID vs selfie)", an.scores?.faceMatch, 40)}
                      {an.riskMax >= 100 && scoreRow("Liveness", an.scores?.liveness, 15)}
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 14 }}>
                        <b>Total</b>
                        <b>{an.riskScore} / {an.riskMax}</b>
                      </div>
                      {an.riskMax < 100 && (
                        <div style={{ fontSize: 11, color: "#8A857F" }}>
                          Old-build submission (no selfie) — scored out of 45.
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: 14, fontSize: 13 }}>
                    <b>Document data (OCR)</b>
                    <div style={{ marginTop: 6, color: "#1A1614" }}>
                      <div>ID numbers found: {an.ocr && an.ocr.idNumbers && an.ocr.idNumbers.length ? an.ocr.idNumbers.join(", ") : "none"}</div>
                      <div>Dates found: {an.ocr && an.ocr.dates && an.ocr.dates.length ? an.ocr.dates.join(" · ") : "none"}</div>
                      <div>Registered name on document: {an.ocr && an.ocr.nameMatchPct != null ? `${an.ocr.nameMatchPct}%` : "—"}</div>
                      {an.ocr && an.ocr.mrz && (
                        <div style={{ marginTop: 4, background: "#FAF7F0", padding: 8, borderRadius: 8 }}>
                          MRZ{an.ocr.mrz.valid ? " (valid)" : " (check digits failed)"}: {an.ocr.mrz.firstName} {an.ocr.mrz.lastName} · doc {an.ocr.mrz.documentNumber} · DOB {an.ocr.mrz.birthDate} · exp {an.ocr.mrz.expirationDate} · {an.ocr.mrz.nationality} {an.ocr.mrz.sex}
                        </div>
                      )}
                    </div>
                  </div>
                  {(an.duplicates?.face?.length > 0 || an.duplicates?.idNumber?.length > 0) && (
                    <div style={{ marginTop: 14, fontSize: 13, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: 10 }}>
                      <b style={{ color: "#B91C1C" }}>Possible duplicates</b>
                      {(an.duplicates.face || []).map((d) => (
                        <div key={"f" + d.userId}>
                          Same face ({d.similarityPct}%): {d.name || d.userId} {d.email ? `· ${d.email}` : ""} · KYC {d.kycStatus || "none"}
                        </div>
                      ))}
                      {(an.duplicates.idNumber || []).map((d) => (
                        <div key={"i" + d.userId}>
                          Same ID number: {d.name || d.userId} {d.email ? `· ${d.email}` : ""} · KYC {d.kycStatus || "none"}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 12, fontSize: 12, color: "#8A857F" }}>
                    Doc quality — front: {an.quality?.front ? `${an.quality.front.width}×${an.quality.front.height}, sharpness ${an.quality.front.sharpness}, glare ${an.quality.front.glarePct}%` : "—"}
                    {an.quality?.back ? ` · back: ${an.quality.back.width}×${an.quality.back.height}, sharpness ${an.quality.back.sharpness}, glare ${an.quality.back.glarePct}%` : ""}
                    {an.deviceAccounts != null ? ` · accounts on this device: ${an.deviceAccounts}` : ""}
                    {an.analyzedAt ? ` · analyzed ${dayjs(an.analyzedAt).format("YYYY-MM-DD HH:mm")}` : ""}
                  </div>
                  {an.autoApproved && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "#047857", fontWeight: 700 }}>
                      ⚡ Auto-approved by the risk engine
                    </div>
                  )}
                  {(analysisUser.kycStatus === "pending" || analysisUser.kycStatus === "resubmit") && (
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
                      <button onClick={() => { setAnalysisUser(null); openDecision(analysisUser, "approved"); }} style={btnApprove}>Approve</button>
                      <button onClick={() => { setAnalysisUser(null); openDecision(analysisUser, "rejected"); }} style={btnReject}>Reject</button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {previewUrl && (
        <div
          style={modalBg}
          onClick={() => setPreviewUrl(null)}
        >
          <img src={previewUrl} alt="" style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8 }} />
        </div>
      )}

      {decisionOpen && decisionUser && (
        <div style={modalBg}>
          <div style={modalCard}>
            <h3 style={{ marginTop: 0 }}>
              {decisionChoice === "approved" ? "Approve" : decisionChoice === "resubmit" ? "Request new images from" : "Reject"}{" "}
              {decisionUser.firstName} {decisionUser.lastName}
            </h3>
            {(decisionChoice === "rejected" || decisionChoice === "resubmit") && (
              <textarea
                placeholder="Reason — shown to the user (required)"
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
                rows={4}
                style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #E5E7EB", marginTop: 8 }}
              />
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setDecisionOpen(false)} style={btnCancel}>Cancel</button>
              <button
                onClick={submitDecision}
                style={decisionChoice === "approved" ? btnApprove : decisionChoice === "resubmit" ? btnResubmit : btnReject}
              >
                Confirm {decisionChoice === "approved" ? "Approve" : decisionChoice === "resubmit" ? "Request" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const th = { padding: 10, textAlign: "left", fontSize: 13, fontWeight: 600, color: "#1A1614" };
const td = { padding: 10, fontSize: 13, color: "#1A1614" };
const btnApprove = { padding: "5px 12px", border: "none", borderRadius: 6, background: "#10B981", color: "#FFF", cursor: "pointer", fontWeight: 600 };
const btnReject = { padding: "5px 12px", border: "none", borderRadius: 6, background: "#EF4444", color: "#FFF", cursor: "pointer", fontWeight: 600 };
const btnResubmit = { padding: "5px 12px", border: "none", borderRadius: 6, background: "#6366F1", color: "#FFF", cursor: "pointer", fontWeight: 600 };
const pagerBtn = (disabled) => ({
  padding: "5px 12px",
  border: "1px solid #E5E7EB",
  borderRadius: 6,
  background: disabled ? "#F3F4F6" : "#FFF",
  color: disabled ? "#9CA3AF" : "#1A1614",
  cursor: disabled ? "default" : "pointer",
  fontSize: 13,
});
const btnCancel = { padding: "6px 14px", border: "1px solid #E5E7EB", borderRadius: 6, background: "#FFF", color: "#1A1614", cursor: "pointer" };
const modalBg = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalCard = { background: "#FFF", padding: 20, borderRadius: 12, width: 480, maxWidth: "90vw" };

export default KycQueue;
